import React, { useRef, useState, useEffect } from 'react';
import {
  AlertHistoryDropDown,
  AlertHistoryItem,
  ContentHeader,
  ItemNotFound,
  SearchItems,
} from '..';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { useMediaQuery } from '@chakra-ui/react';
import { useAuth } from '../../context/auth.context';
import AlertsFiredService from '../../services/alertsFired.service';
import InfiniteScroll from 'react-infinite-scroller';

const ITEMS_PER_PAGE = 15;

const AlertHistoryComponent = () => {
  const { currentUser, authPending } = useAuth();
  const [fetchingAlertItems, setFetchingAlertItems] = useState(false);
  const [isMobileSize] = useMediaQuery('(max-width: 425px)');
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmpty, setShowEmpty] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allAlertItems, setAllAlertItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [products, setProducts] = useState([]);
  const scrollContainerRef = useRef(null);
  // Guards against stale responses when filters change mid-flight.
  const requestIdRef = useRef(0);
  // Skips the debounced refetch effect on the very first render.
  const didMountRef = useRef(false);

  // Number each alert as the Nth price drop for its product. NOTE: this is
  // computed over the alerts currently loaded into memory, so under pagination
  // it is approximate until a product is selected (which loads only that
  // product's alerts). Precise counts would require a server-side computation.
  const calculateAlertCounters = (items) => {
    const groupedByProduct = {};
    items.forEach((item) => {
      if (!item.productId) return;
      if (!groupedByProduct[item.productId]) {
        groupedByProduct[item.productId] = [];
      }
      groupedByProduct[item.productId].push(item);
    });

    return items.map((item) => {
      if (!item.productId) return { ...item, counter: 0 };
      const group = groupedByProduct[item.productId];
      const sortedGroup = group.sort(
        (a, b) => new Date(a.scrapedAt) - new Date(b.scrapedAt)
      );
      const counter = sortedGroup.findIndex((i) => i === item) + 1;
      return { ...item, counter };
    });
  };

  // Fetch one page with the current filters applied server-side.
  // reset=true replaces the list (first load / filter change);
  // otherwise the page is appended (infinite scroll).
  const fetchAlerts = async (pageNo, { reset }) => {
    if (!currentUser?._id) return;

    const requestId = ++requestIdRef.current;
    setFetchingAlertItems(true);

    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        pageNo,
        q: searchQuery,
      };
      if (selectedItem?.productId) params.productId = selectedItem.productId;

      const info = await AlertsFiredService.getAlertsByUserId(
        currentUser._id,
        params
      );

      // A newer filter/search has superseded this request — drop the result.
      if (requestId !== requestIdRef.current) return;

      const records = info.records || [];
      setHasMoreItems(Boolean(info.hasMoreItems));

      setAllAlertItems((prev) => {
        const base = reset ? [] : prev;
        const existingIds = new Set(base.map((i) => i._id));
        const merged = [
          ...base,
          ...records.filter((r) => !existingIds.has(r._id)),
        ];
        return calculateAlertCounters(merged);
      });
      setCurrentPage(pageNo);
      setShowEmpty(reset && records.length === 0);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error(
          'Error loading alerts:',
          error.response ? error.response.data : error.message
        );
        setShowEmpty(true);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setFetchingAlertItems(false);
      }
    }
  };

  const loadMoreAlertItems = () => {
    if (fetchingAlertItems || !hasMoreItems || !currentUser?._id) return;
    fetchAlerts(currentPage + 1, { reset: false });
  };

  const handleSelectItem = (product) => {
    setSelectedItem(product);
  };

  const handleClearSelection = () => {
    if (!selectedItem) return;
    setSelectedItem(null);
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
  };

  // Initial load + populate the product filter dropdown.
  useEffect(() => {
    if (authPending || !currentUser?._id) return;
    fetchAlerts(1, { reset: true });

    (async () => {
      try {
        const prods = await AlertsFiredService.getProducts(currentUser._id);
        setProducts(prods || []);
      } catch (error) {
        console.error('Error fetching alert products:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authPending, currentUser?._id]);

  // Refetch from page 1 whenever a filter changes; debounce the text search.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (authPending || !currentUser?._id) return;
    const handler = setTimeout(() => {
      fetchAlerts(1, { reset: true });
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedItem]);

  if (authPending) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <ContentHeader
        title="Alert History"
        description="Review all your saved items, set price change alerts"
        headingStyle={{
          color: 'purchr.brown.dark',
          fontSize: { base: '32px', sm: '44px' },
          fontWeight: '700',
        }}
        descriptionStyle={{
          fontWeight: '400',
          fontSize: '14px',
          color: '#C0C0C0',
          display: { base: 'none', md: 'block' },
        }}
      />

      <SearchItems filterItemDatas={[]} handleSearch={handleSearch}>
        <AlertHistoryDropDown
          currentPageAlertItems={products} // distinct products from the server
          selectedItem={selectedItem}
          handleSelectItem={handleSelectItem}
          handleClearSelection={handleClearSelection}
        />
      </SearchItems>

      <Box
        mt={{ base: '0', sm: '8px' }}
        height={{
          base: `calc(${window.innerHeight}px - 200px)`,
          lg: `calc(${window.innerHeight}px - 300px)`,
        }}
        overflowY="auto"
        style={{ scrollbarWidth: isMobileSize ? 'none' : 'thin' }}
        ref={scrollContainerRef}
      >
        {fetchingAlertItems && allAlertItems.length === 0 ? (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <Spinner size="md" />
          </Flex>
        ) : showEmpty ? (
          <ItemNotFound />
        ) : (
          <InfiniteScroll
            loadMore={loadMoreAlertItems}
            getScrollParent={() => scrollContainerRef.current}
            threshold={0}
            hasMore={!fetchingAlertItems && hasMoreItems}
            useWindow={false}
          >
            {allAlertItems.map((alertItem) =>
              alertItem ? (
                <AlertHistoryItem
                  key={alertItem._id}
                  alertItem={alertItem}
                  showOriginalPrice={true}
                  counter={alertItem.counter || 0}
                />
              ) : null
            )}
          </InfiniteScroll>
        )}
        {fetchingAlertItems && hasMoreItems && allAlertItems.length > 0 ? (
          <Flex my="10px" alignItems="center" justifyContent="center">
            <Spinner size="md" />
          </Flex>
        ) : null}
      </Box>
    </Box>
  );
};

export default AlertHistoryComponent;
